import React, { useState } from 'react'
import { ShoppingCart, Star, Truck, TruckIcon } from 'lucide-react'
import {
	ProductEntity,
	AttributeProductEntity,
	Paymethods,
	NameAttributes,
	colorsMap,
	sizeMap,
	flavorMap,
	objectivesMap,
	modalitiesMap,
	attributeMaps,
} from '@/entities/api/products/product'

import type {} from '@/entities/api/products/product'
import formatCurrency from '@/utils/price-convert'
import { API_URL } from '@/services/api/keys'
import { verifyStockOfAttribute } from '@/services/api/products/attributes'
import Toast from '../elements/toast'
import CartEntity from '@/entities/api/cart/cart'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import { v4 as uuidv4 } from 'uuid'
import { addToCart } from '@/store/features/cart'
import { createCartProduct } from '@/services/api/cart/cart'
import CustomersEntity from '@/entities/api/customers/customer'

interface ProductCardProps {
	product: ProductEntity
	className?: string
	customerId: string
	customer: CustomersEntity | null
	click: () => void
}

const ProductCard: React.FC<ProductCardProps> = ({
	product,
	customerId,
	className = '',
	customer,
	click,
}) => {
	const [selectedAttribute, setSelectedAttribute] = useState(0)
	const [isHovered, setIsHovered] = useState(false)
	const dispatch = useDispatch<AppDispatch>()
	const token = useSelector((state: RootState) => state.auth.token)

	const hasDiscount = product.discount > 0
	const hasVariants = product.attributes && product.attributes.length > 0
	const hasFreeShipping = product.shipping_info.type === Paymethods.free

	const normalizeAttribute = (attribute: any) => {
		if (!attribute) return null

		const names = Array.isArray(attribute.name)
			? attribute.name
			: [attribute.name]
		const values = Array.isArray(attribute.value)
			? attribute.value
			: [attribute.value]
		const references = Array.isArray(attribute.reference)
			? attribute.reference
			: [attribute.reference]

		if (names.length !== values.length) {
			const valuesToPad = names.length - values.length
			for (let i = 0; i < valuesToPad; i++) {
				values.push(values[0] || '')
			}
		}

		if (names.length !== references.length) {
			for (let i = references.length; i < names.length; i++) {
				const name = names[i]
				const value = values[i]
				let referenceValue = String(value)

				switch (name) {
					case NameAttributes.COLOR:
						referenceValue =
							colorsMap[value as keyof typeof colorsMap] || String(value)
						break
					case NameAttributes.SIZE:
						referenceValue =
							sizeMap[value as keyof typeof sizeMap] || String(value)
						break
					case NameAttributes.FLAVOR:
						referenceValue =
							flavorMap[value as keyof typeof flavorMap] || String(value)
						break
					case NameAttributes.OBJECTIVE:
						referenceValue =
							objectivesMap[value as keyof typeof objectivesMap] ||
							String(value)
						break
					case NameAttributes.MODALITY:
						referenceValue =
							modalitiesMap[value as keyof typeof modalitiesMap] ||
							String(value)
						break
				}

				references.push(referenceValue)
			}
		}

		return {
			...attribute,
			name: names,
			value: values,
			reference: references,
		}
	}

	const getAttributeDisplayValue = (attribute: any): string => {
		try {
			const normalized = normalizeAttribute(attribute)
			if (!normalized) return ''

			const values = normalized.value
			const names = normalized.name

			if (values.length === 0) return ''

			const displayValues = values.map((value: any, index: number) => {
				const name = names[index] || names[0]
				const valueStr = String(value)

				switch (name) {
					case NameAttributes.COLOR:
						return colorsMap[valueStr as keyof typeof colorsMap]
							? valueStr
							: valueStr
					case NameAttributes.SIZE:
						return sizeMap[valueStr as keyof typeof sizeMap]
							? valueStr
							: valueStr
					case NameAttributes.FLAVOR:
						return flavorMap[valueStr as keyof typeof flavorMap] || valueStr
					case NameAttributes.OBJECTIVE:
						return (
							objectivesMap[valueStr as keyof typeof objectivesMap] || valueStr
						)
					case NameAttributes.MODALITY:
						return (
							modalitiesMap[valueStr as keyof typeof modalitiesMap] || valueStr
						)
					default:
						return (
							attributeMaps[valueStr as keyof typeof attributeMaps] || valueStr
						)
				}
			})

			return displayValues.join(' / ')
		} catch (error) {
			const fallbackValue = Array.isArray(attribute.value)
				? attribute.value[0]
				: attribute.value
			return (
				attributeMaps[String(fallbackValue) as keyof typeof attributeMaps] ||
				String(fallbackValue || '')
			)
		}
	}

	const currentAttribute =
		hasVariants && product.attributes?.[selectedAttribute]
			? product.attributes[selectedAttribute]
			: null

	const getAttributePrice = (): number => {
		return currentAttribute?.price || 0
	}

	const calculateDiscountedPrice = (
		price: number,
		discount: number
	): number => {
		return price - (price * discount) / 100
	}

	const basePrice = getAttributePrice()
	const discountedPrice = hasDiscount
		? calculateDiscountedPrice(basePrice, product.discount)
		: basePrice

	const getCurrentImage = (): string => {
		if (product.images?.[0]?.url) {
			return `${API_URL}/${product.images[0].url}`
		}
		if (currentAttribute?.image?.url) {
			return `${API_URL}/${currentAttribute.image.url}`
		}
		return 'https://via.placeholder.com/400x400?text=No+Image'
	}

	const getCurrentStock = (): number => {
		return currentAttribute?.quantity || 0
	}

	const currentStock = getCurrentStock()
	const isOutOfStock = hasVariants ? currentStock === 0 : false

	const getAverageRating = () => {
		if (!product.raitings || product.raitings.length === 0) {
			return { average: 0, count: 0 }
		}

		const validRatings = product.raitings.filter(
			(rating) => !rating._deleted && rating.rate
		)
		const sum = validRatings.reduce((acc, rating) => acc + rating.rate, 0)
		const average = validRatings.length > 0 ? sum / validRatings.length : 0

		return {
			average: Math.round(average * 10) / 10,
			count: validRatings.length,
		}
	}

	const { average: averageRating, count: reviewCount } = getAverageRating()

	const getAttributeReference = (attribute: AttributeProductEntity): string => {
		try {
			const normalized = normalizeAttribute(attribute)
			if (!normalized) return String(attribute.value || '')

			const references = normalized.reference
			const values = normalized.value

			if (references.length > 0) {
				return references.join(' / ')
			}

			return values.join(' / ')
		} catch (error) {
			const fallbackValue = Array.isArray(attribute.value)
				? attribute.value[0]
				: attribute.value
			return String(fallbackValue || '')
		}
	}

	const getAttributeShortDisplay = (
		attribute: AttributeProductEntity
	): string => {
		try {
			const normalized = normalizeAttribute(attribute)
			if (!normalized) return ''

			const values = normalized.value
			if (values.length === 0) return ''

			if (values.length === 1) {
				return String(values[0]).slice(0, 2).toUpperCase()
			}

			return values
				.map((v: any) => String(v).slice(0, 1))
				.join('')
				.toUpperCase()
		} catch (error) {
			const fallbackValue = Array.isArray(attribute.value)
				? attribute.value[0]
				: attribute.value
			return String(fallbackValue || '')
				.slice(0, 2)
				.toUpperCase()
		}
	}

	const onAddToCart = async (product: ProductEntity) => {
		const response = await verifyStockOfAttribute(
			product?._id ?? '',
			product?.attributes[0]?._id ?? ''
		)

		if ([400, 401, 409, 404].includes(response?.status ?? 0)) {
			Toast({
				type: 'error',
				message: 'El producto no se puede agregar al carrito',
			})
			return
		}

		const { quantity: quantityFromApi } = response?.data

		if (1 > quantityFromApi) {
			Toast({
				type: 'info',
				message: `Solo quedan ${quantityFromApi} unidades disponibles`,
			})
			return
		}

		const attribute: AttributeProductEntity = {
			...product?.attributes[0],
			quantity: 1,
		}

		const normalizedAttribute = normalizeAttribute(attribute)
		if (!normalizedAttribute) {
			Toast({
				type: 'error',
				message: 'Error al procesar el atributo del producto',
			})
			return
		}

		const cart: CartEntity = {
			product: {
				...product,
				attributes: [{ ...normalizedAttribute }],
			},
			customer: customerId
				? {
						_id: customer?._id,
						role: customer?.role,
						name: customer?.name,
						lastname: customer?.lastname,
						images: customer?.images ? customer?.images : [],
						email: customer?.email,
						phone: customer?.phone,
						_created: customer?._created,
				  }
				: {
						_id: uuidv4(),
						name: 'default',
						lastname: 'default',
				  },
		}

		if (!customer) {
			cart._id = uuidv4()
			cart.isLocal = true
			cart._created = new Date().toDateString()
			cart._modified = new Date().toDateString()
			cart._deleted = false
		}

		if (customerId) {
			const body = {
				product: product?._id,
				attribute: normalizedAttribute._id,
				customer: customerId,
				quantity: 1,
			}

			const response = await createCartProduct(body, token ?? '')

			if ([400, 401, 409, 404].includes(response.status ?? 0)) {
				Toast({
					type: 'error',
					message:
						'No se pudo agregar el producto al carrito, contacta con soporte',
				})
				return
			}

			dispatch(addToCart({ cartItem: response?.data?.data }))
		} else {
			dispatch(addToCart({ cartItem: cart }))
		}

		Toast({
			type: 'info',
			message: `${product?.name} x 1 agregado al carrito correctamente.`,
		})
	}

	return (
		<div
			className={`group cursor-pointer relative bg-white rounded-lg md:rounded-xl border border-gray-200 overflow-hidden shadow-sm transition-all duration-300 hover:shadow-lg ${className}`}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<div className='relative aspect-square overflow-hidden bg-gray-50'>
				<img
					src={getCurrentImage()}
					alt={product.name}
					className={`w-full h-full object-cover transition-transform duration-300 ${
						isHovered ? 'scale-110' : 'scale-100'
					}`}
					loading='lazy'
					onClick={click}
				/>

				{hasDiscount && (
					<div className='absolute top-1 left-1 md:top-2 md:left-2 bg-red-500 text-white px-1 py-0.5 md:px-2 md:py-1 rounded md:rounded-md text-xs font-bold shadow-md z-10'>
						-{product.discount}%
					</div>
				)}

				<div className='absolute top-1 right-1 md:top-2 md:right-2 bg-gray-900/80 backdrop-blur-sm text-white px-1 py-0.5 md:px-2 md:py-1 rounded md:rounded-md text-xs font-medium'>
					<span className='hidden sm:inline'>{product.category.name}</span>
					<span className='sm:hidden'>
						{product.category.name.slice(0, 8)}...
					</span>
				</div>

				{product?.shipping_info?.isHaveShipping && hasFreeShipping && (
					<div className='absolute top-6 right-1 md:top-10 md:right-2 bg-green-500 text-white px-1 py-0.5 md:px-2 md:py-1 rounded md:rounded-md text-xs font-medium flex items-center gap-0.5 md:gap-1'>
						<Truck size={8} className='md:hidden' />
						<Truck size={10} className='hidden md:inline' />
						<span className='hidden sm:inline'>Envío gratis</span>
						<span className='sm:hidden'>Gratis</span>
					</div>
				)}

				{isOutOfStock && (
					<div className='absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center'>
						<span className='bg-gray-800 text-white px-2 py-1 md:px-4 md:py-2 rounded-lg font-semibold text-xs md:text-sm'>
							Agotado
						</span>
					</div>
				)}

				{!isOutOfStock && (
					<button
						onClick={() => onAddToCart(product)}
						className='absolute cursor-pointer bottom-1 right-1 md:bottom-2 md:right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 md:p-2.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300'
						aria-label='Agregar al carrito'
					>
						<ShoppingCart size={14} className='md:hidden' />
						<ShoppingCart size={18} className='hidden md:inline' />
					</button>
				)}
			</div>

			<div className='p-2 md:p-3 space-y-1 md:space-y-2 '>
				<div
					className='text-xs text-gray-500 font-medium uppercase tracking-wide'
					onClick={click}
				>
					<span className='hidden sm:inline'>{product.brand}</span>
					<span className='sm:hidden'>{product.brand.slice(0, 12)}...</span>
				</div>

				<h3
					className='font-semibold text-gray-900 text-xs md:text-sm leading-tight line-clamp-2 min-h-[2rem] md:min-h-[2.5rem]'
					onClick={click}
				>
					{product.name}
				</h3>

				{reviewCount > 0 && (
					<div className='flex items-center gap-0.5 md:gap-1' onClick={click}>
						<div className='flex items-center'>
							{[...Array(5)].map((_, i) => (
								<Star
									key={i}
									size={10}
									className={`md:w-3 md:h-3 ${
										i < Math.floor(averageRating)
											? 'text-yellow-400 fill-current'
											: 'text-gray-300'
									}`}
								/>
							))}
						</div>
						<span className='text-xs text-gray-500'>
							<span className='hidden sm:inline'>
								{averageRating} ({reviewCount})
							</span>
							<span className='sm:hidden'>{averageRating}</span>
						</span>
					</div>
				)}

				{hasVariants && (
					<div className='space-y-0.5 md:space-y-1'>
						<p className='text-xs text-gray-600 font-medium hidden md:block'>
							Variantes:
						</p>
						<div className='flex gap-0.5 md:gap-1 flex-wrap'>
							{product.attributes?.slice(0, 3).map((attr, index) => (
								<button
									key={attr._id || index}
									onClick={() => setSelectedAttribute(index)}
									className={`w-8 h-8 md:w-16 md:h-16 rounded border-2 transition-all duration-200 flex items-center justify-center text-xs font-medium ${
										selectedAttribute === index
											? 'border-red-500 ring-1 ring-red-500'
											: 'border-gray-200 hover:border-gray-300'
									}`}
									style={{
										backgroundImage: attr.image?.url
											? `url(${API_URL}/${attr.image.url})`
											: undefined,
										backgroundColor: !attr.image?.url ? '#f3f4f6' : undefined,
										backgroundSize: 'cover',
										backgroundPosition: 'center',
									}}
									title={getAttributeReference(attr)}
									aria-label={`Seleccionar variante ${getAttributeDisplayValue(
										attr
									)}`}
								>
									{!attr.image?.url && (
										<span className='text-xs truncate'>
											{getAttributeShortDisplay(attr)}
										</span>
									)}
								</button>
							))}
							{product.attributes && product.attributes.length > 3 && (
								<div className='w-8 h-8 md:w-6 md:h-6 rounded border-2 border-gray-200 bg-gray-100 flex items-center justify-center'>
									<span className='text-xs text-gray-500 font-medium'>
										+{product.attributes.length - 3}
									</span>
								</div>
							)}
						</div>
					</div>
				)}

				{basePrice > 0 && (
					<div className='space-y-0.5 md:space-y-1' onClick={click}>
						<div className='flex items-center gap-1 md:gap-2'>
							{hasDiscount && (
								<span className='text-xs md:text-sm text-gray-500 line-through'>
									{formatCurrency(basePrice)}
								</span>
							)}
							<span
								className={`font-bold text-sm md:text-lg ${
									hasDiscount ? 'text-red-600' : 'text-gray-900'
								}`}
							>
								{formatCurrency(discountedPrice)}
							</span>
						</div>
					</div>
				)}

				<div
					className='flex items-center gap-0.5 md:gap-1 text-xs'
					onClick={click}
				>
					{product?.shipping_info?.isHaveShipping && hasFreeShipping ? (
						<>
							<Truck size={10} className='md:w-3 md:h-3 text-green-600' />
							<span className='text-green-600 font-medium'>
								<span className='hidden sm:inline'>Envío gratis</span>
								<span className='sm:hidden'>Gratis</span>
							</span>
						</>
					) : (
						product?.shipping_info?.price > 0 && (
							<>
								<TruckIcon size={10} className='md:w-3 md:h-3 text-gray-500' />
								<span className='text-gray-500 text-xs'>
									<span className='hidden sm:inline'>
										Envío: {formatCurrency(product.shipping_info.price)}
									</span>
									<span className='sm:hidden'>
										{formatCurrency(product.shipping_info.price)}
									</span>
								</span>
							</>
						)
					)}
				</div>

				{hasVariants && currentAttribute && (
					<div
						className='flex items-center justify-between text-xs'
						onClick={click}
					>
						<span
							className={`flex items-center gap-0.5 md:gap-1 ${
								currentStock > 5
									? 'text-green-600'
									: currentStock > 0
									? 'text-orange-600'
									: 'text-red-600'
							}`}
						>
							<div
								className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${
									currentStock > 5
										? 'bg-green-500'
										: currentStock > 0
										? 'bg-orange-500'
										: 'bg-red-500'
								}`}
							/>
							<span className='hidden sm:inline'>
								{currentStock > 5
									? 'En stock'
									: currentStock > 0
									? `Últimas ${currentStock} unidades`
									: 'Agotado'}
							</span>
							<span className='sm:hidden'>
								{currentStock > 5
									? 'Stock'
									: currentStock > 0
									? `${currentStock} uds`
									: 'Agotado'}
							</span>
						</span>
					</div>
				)}
			</div>
		</div>
	)
}

export default ProductCard
